import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ChannelHistory } from '@models/channel-history';
import { DashboardPanelInterface } from '@models/dashboard-panel-interface';
import { ChannelHistoryService } from '@service/channel-history.service';
import { TrendGraphSettingsService } from '@service/trend-graph-settings.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import { Observable, lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-dashboard-panel-trend-graph',
    templateUrl: './dashboard-panel-trend-graph.component.html',
    styleUrls: ['./dashboard-panel-trend-graph.component.scss'],
    providers: [ChannelHistoryService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DashboardPanelTrendGraphComponent
    implements DashboardPanelInterface, OnInit, OnDestroy, AfterContentInit
{
    @ViewChild('graphContainer', { static: true }) graphContainer: ElementRef;
    @Output() onDeleteClicked = new EventEmitter<void>();
    @Input() graphId: string = '';
    @Input() isPreview: boolean = false;

    onHistoryUpdated: Observable<ChannelHistory>;
    onResourceNotFound = new EventEmitter<void>();

    graphWidth: number = 150;
    graphHeight: number = 100;

    _view: TrendGraphView;
    updateInterval: NodeJS.Timeout = null;

    isGraphLoaded = false;
    isInitComplete = false;

    MIN_DATE_INDEX = 0;
    MAX_DATE_INDEX = 1999;

    dateRange = {
        startIndex: this.MIN_DATE_INDEX,
        endIndex: this.MAX_DATE_INDEX,
    };

    constructor(
        private graphService: TrendGraphViewsService,
        private channelHistoryService: ChannelHistoryService,
        private trendGraphSettings: TrendGraphSettingsService,
        private cdr: ChangeDetectorRef,
    ) {
        this.onHistoryUpdated = this.channelHistoryService.onHistoryUpdated();
    }

    async ngOnInit(): Promise<void> {
        let views = await lastValueFrom(this.graphService.getAll());
        this._view = views.find((v) => v.id === this.graphId);

        if (this._view) {
            this.channelHistoryService.setWatchedChannels(
                this._view.channels.map((channelView) => channelView.fcuChannelId),
            );
            this.startUpdating();
            this.isInitComplete = true;
        } else {
            this.emitResourceNotFound();
        }
        this.graphWidth = this.graphContainer?.nativeElement?.clientWidth || this.graphWidth;
        this.graphHeight = this.graphContainer?.nativeElement?.clientHeight || this.graphHeight;
        this.cdr.markForCheck();
    }

    ngAfterViewInit(): void {
        this.graphWidth = this.graphContainer?.nativeElement?.clientWidth || this.graphWidth;
        this.graphHeight = this.graphContainer?.nativeElement?.clientHeight || this.graphHeight;
        this.cdr.markForCheck();
        
        window.addEventListener('resize', (event) => {
            this.graphWidth = this.graphContainer?.nativeElement?.clientWidth || this.graphWidth;
            this.graphHeight = this.graphContainer?.nativeElement?.clientHeight || this.graphHeight;
            this.cdr.markForCheck();
        });
    }

    ngAfterContentInit(): void {

        this.graphWidth = this.graphContainer?.nativeElement?.clientWidth || this.graphWidth;
        this.graphHeight = this.graphContainer?.nativeElement?.clientHeight || this.graphHeight;
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        this.stopUpdating();
        this.channelHistoryService.Cleanup();
    }

    startUpdating() {
        if (this.updateInterval === null) {
            this.updateInterval = setInterval(() => {
                this.channelHistoryService.refresh();
            }, this.trendGraphSettings.updateInterval);
        }
    }

    stopUpdating() {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
    }

    handleTrendGraphLoadingComplete() {
        this.isGraphLoaded = true;
        this.channelHistoryService.refresh();
        this.cdr.markForCheck();
    }

    handleDeleteClicked() {
        this.emitDeleteCLicked();
        this.cdr.markForCheck();
    }

    emitDeleteCLicked() {
        this.onDeleteClicked.emit();
    }

    emitResourceNotFound() {
        this.onResourceNotFound.emit();
    }

    isAtDefaultZoom() {
        return (
            this.dateRange.startIndex == this.MIN_DATE_INDEX &&
            this.dateRange.endIndex == this.MAX_DATE_INDEX
        );
    }

    resetZoom() {
        this.dateRange.startIndex = this.MIN_DATE_INDEX;
        this.dateRange.endIndex = this.MAX_DATE_INDEX;
        this.cdr.markForCheck();
    }

    public get isLoadingComplete() {
        return this.isInitComplete && this.isGraphLoaded;
    }
    public get view() {
        return this._view;
    }
    public get title() {
        return this._view?.name;
    }
}
